/**
 * AdminShell — Phase 1 chrome for the /admin/* route tree.
 *
 * BigCommerce-style left sidebar: slate #374751 background (hardcoded —
 * NOT `bg-pp-slate` because that token flips to #E2E6EA in dark mode and
 * destroys contrast against the hardcoded `text-[#D5DEE4]` inactive nav
 * items). The admin sidebar is a constant-dark chrome surface across both
 * light and dark themes — that's the design intent and the
 * accessibility floor (AAA 7.06:1 for inactive items in BOTH modes).
 * White-ish text, purple #574B8C active item with lavender left-border
 * accent. No marketing nav anywhere — the slate sidebar IS the admin
 * indicator (the legacy red `<AdminModeBar>` is now reserved for
 * impersonation per Backlog D coordination).
 *
 * VR-note (2026-02-27): if the sidebar bg ever needs to follow OS theme,
 * also flip the inactive text + icon color via a token; today both fg
 * AND bg are constant so the contrast invariant holds.
 *
 * Phase 3 §1 — accepts an optional `children` prop. When provided
 * (via `<PublicPageRouter>` for an authed admin viewing a public
 * route), renders that content in the main slot; otherwise mounts
 * the React Router `<Outlet>` for the admin sub-routes. Sidebar
 * items show no active state when wrapping a public page — matches
 * the existing detail-panel-not-in-sidebar pattern.
 *
 * Phase 3 §5 — at `<md:`, the sidebar collapses behind a hamburger
 * button in the topbar that opens a left-side `<Sheet>` containing
 * the same 9 nav items. AvatarDropdown stays inline so logout is
 * one tap away without opening the drawer.
 */
import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import AvatarDropdown from "@/components/AvatarDropdown";
import ThemeToggle from "@/components/ThemeToggle";
import AdminViewBar from "@/components/admin/AdminViewBar";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  LayoutDashboard,
  CalendarRange,
  BarChart3,
  Users,
  PawPrint,
  Coins,
  Layers,
  Receipt,
  Settings,
  Shield,
  Menu,
  MessageSquare,
  MapPinned,
  ScrollText,
  Trophy,
  ClipboardList,
  Ticket,
} from "lucide-react";
import { cn } from "@/lib/utils";

const SIDEBAR_ITEMS = [
  { id: "dashboard", label: "Dashboard", to: "/admin/dashboard", icon: LayoutDashboard },
  { id: "bookings", label: "Bookings", to: "/admin/bookings", icon: CalendarRange },
  { id: "analytics", label: "Analytics", to: "/admin/analytics", icon: BarChart3 },
  // Feb 2026 Analytics Commit C2 — first-party Traffic dashboard.
  // Sits directly under Pluckonomics ("Analytics") so an admin can
  // toggle between revenue analytics and traffic funnel analytics.
  { id: "analytics-traffic", label: "Traffic", to: "/admin/analytics/traffic", icon: BarChart3 },
  // Rate My Pluck moderation — between Analytics and Accounts per Step 1 §11.
  { id: "reviews", label: "Reviews", to: "/admin/reviews", icon: MessageSquare },
  // F.6 — Service Areas (coverage ZIPs admin).
  { id: "service-areas", label: "Service Areas", to: "/admin/service-areas", icon: MapPinned },
  { id: "accounts", label: "Accounts", to: "/admin/accounts", icon: Users },
  { id: "poopartners", label: "PooPartners", to: "/admin/poopartners", icon: PawPrint },
  { id: "poobucks", label: "PooBucks", to: "/admin/poobucks", icon: Coins },
  // Cutest Pluck Contest — Phase C surface (config/moderation/ranking)
  // shipped without a sidebar entry, so admins could not discover it.
  // Landing on `/admin/contest/config` because dates are the launch-
  // blocking surface; the shared `AdminContestTabs` inside each contest
  // page lets admins jump to entries + ranking. Positioned right after
  // PooBucks because contest ranking credits PooBucks — the two admin
  // flows share ledger context.
  { id: "contest", label: "Contest", to: "/admin/contest/config", icon: Trophy },
  // Pre-Launch Demand Capture (2026-07-21) — waitlist roster + CSV.
  // Positioned adjacent to Contest since both drive pre-launch demand.
  { id: "waitlist", label: "Waitlist", to: "/admin/waitlist", icon: ClipboardList },
  { id: "tiers-and-plans", label: "Tiers & plans", to: "/admin/tiers-and-plans", icon: Layers },
  { id: "fees", label: "Fees", to: "/admin/fees", icon: Receipt },
  // Item 2 (Promo Codes) — platform-wide discount codes. Positioned
  // near Fees + PooBucks because it shares financial-instrument
  // context (commission-clamped absorption).
  { id: "promo-codes", label: "Promo codes", to: "/admin/promo-codes", icon: Ticket },
  // Admin-Audit-Browser dispatch — cross-cutting `audit_events` browser.
  // Sits above Settings so ops-heavy items (Dashboard → Fees → Audit)
  // stay grouped and Settings remains the natural "config bottom".
  { id: "audit-events", label: "Audit events", to: "/admin/audit-events", icon: ScrollText },
  { id: "settings", label: "Settings", to: "/admin/settings", icon: Settings },
];

// ---------------------------------------------------------------------------
// Sidebar nav body — extracted so both the desktop <aside> and the
// mobile <Sheet> reuse the same item rendering.
// ---------------------------------------------------------------------------
function SidebarNavBody({ pathname, onItemClick, mobile = false }) {
  return (
    <ul className={cn("flex flex-col gap-0.5", mobile && "mt-2")}>
      {SIDEBAR_ITEMS.map((it) => {
        const Icon = it.icon;
        const isActive = pathname === it.to || pathname.startsWith(`${it.to}/`);
        return (
          <li key={it.id}>
            <NavLink
              to={it.to}
              onClick={onItemClick}
              className={cn(
                "group flex w-full items-center gap-3 rounded-r-lg border-l-[3px] text-[14px] font-medium transition-colors",
                mobile ? "px-4 py-3 text-[17px]" : "px-4 py-2.5",
                isActive
                  ? "border-[#B0A8D9] bg-[#574B8C] text-white"
                  : "border-transparent text-[#D5DEE4] hover:bg-white/5 hover:text-white"
              )}
              data-testid={`admin-shell-sidebar-${it.id}`}
              data-active={isActive || undefined}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon
                className={cn(
                  "h-4 w-4 shrink-0 transition-colors",
                  isActive ? "text-white" : "text-[#D5DEE4] group-hover:text-white"
                )}
                aria-hidden="true"
              />
              <span className="truncate">{it.label}</span>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

function SidebarBrand() {
  return (
    <Link
      to="/admin"
      className="block px-6 py-6 select-none"
      data-testid="admin-shell-sidebar-brand"
      aria-label="PooPluckers admin home"
    >
      <span
        className="text-[22px] leading-none tracking-[-0.02em]"
        style={{ fontFamily: '"Manrope", system-ui, sans-serif', fontWeight: 800 }}
      >
        <span style={{ color: "var(--pp-orange)" }}>Poo</span>
        <span style={{ color: "white" }}>Pluckers</span>
      </span>
      <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">
        Admin portal
      </span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Desktop sidebar — slate bg, white-ish text, orange active item.
// ---------------------------------------------------------------------------
function AdminSidebar() {
  const { pathname } = useLocation();
  return (
    <aside
      className="hidden md:flex w-[240px] shrink-0 bg-[#374751] text-white flex-col border-r-2 border-white"
      data-testid="admin-shell-sidebar"
      aria-label="Admin navigation"
    >
      <SidebarBrand />
      <nav className="flex-1 px-3 pb-6" role="navigation">
        <SidebarNavBody pathname={pathname} />
      </nav>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Top bar — sits to the right of the sidebar (or full-width on mobile).
// Holds the page title + AvatarDropdown + (mobile only) hamburger.
// ---------------------------------------------------------------------------
function AdminTopBar() {
  const { user } = useAuth();
  const { pricing } = useConfig();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Option D template interpolation — duplicates the same logic used
  // by the legacy AdminPortal heading so the shell title stays per-
  // admin personalized.
  const template = pricing?.admin_portal_heading || "{firstName}'s controls";
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "Admin";
  const headerLabel = template.replace("{firstName}", firstName);

  return (
    <header
      className="flex items-center justify-between border-b border-pp-border bg-pp-v3-bg px-4 md:px-8 py-4"
      data-testid="admin-shell-topbar"
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile hamburger — only renders < md. Same touch target spec
            as SharedNav (44×44) so it matches the existing convention. */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-md text-pp-text"
              style={{ width: 44, height: 44 }}
              aria-label="Open admin menu"
              data-testid="admin-shell-hamburger-btn"
            >
              <Menu size={24} aria-hidden="true" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-[280px] p-0 bg-[#374751] text-white border-none"
            data-testid="admin-shell-mobile-sheet"
          >
            <SidebarBrand />
            <nav className="px-3 pb-6" role="navigation" data-testid="admin-shell-mobile-nav">
              <SidebarNavBody
                pathname={pathname}
                onItemClick={() => setDrawerOpen(false)}
                mobile
              />
            </nav>
          </SheetContent>
        </Sheet>

        <Shield className="h-6 w-6 text-pp-orange shrink-0 hidden sm:block" aria-hidden="true" />
        <h1
          className="font-heading text-xl sm:text-2xl font-semibold text-pp-text truncate"
          data-testid="admin-shell-heading"
        >
          {headerLabel}
        </h1>
      </div>
      <div className="flex items-center gap-3">
        <ThemeToggle />
        <AvatarDropdown />
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Shell — AdminViewBar (orange) atop sidebar + (topbar + outlet|children).
//
// Phase 3 §1: `children` prop allows the shell to wrap a public-page
// element when an authed admin visits `/about` etc. via PublicPageRouter.
// When `children` is provided, no <Outlet/> is rendered.
// ---------------------------------------------------------------------------
export default function AdminShell({ children }) {
  return (
    <div
      className="min-h-screen flex flex-col bg-pp-v3-bg"
      style={{ paddingTop: "var(--admin-mode-bar-h, 0px)" }}
      data-testid="admin-shell"
    >
      <AdminViewBar />
      <div className="flex-1 flex min-h-0">
        <AdminSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <AdminTopBar />
          <main className="flex-1 p-4 md:p-8 overflow-x-hidden" data-testid="admin-shell-main">
            {children || <Outlet />}
          </main>
        </div>
      </div>
    </div>
  );
}
